/* eslint-disable react-hooks/exhaustive-deps */
import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FaBookmark, FaShapes } from 'react-icons/fa';
import { GiAmericanFootballHelmet } from 'react-icons/gi';
import { LuImage, LuType } from 'react-icons/lu';
import { PiBoundingBox } from 'react-icons/pi';
import { useAPIClient } from '../../../context/apiClient';
import {
  useComponentImportDispatch,
  useComponentImportState,
} from '../../../context/componentImport';
import {
  AdElement,
  AdElementImage,
  AdElementTeamAsset,
  AdElementCasinoAsset,
  AdElementText,
  Datasources,
  isImageElement,
  isSVGElement,
  isTeamAssetElement,
  isTextElement,
  isVectorElement,
  isCasinoAssetElement,
} from '@adflow/types';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Icon,
  Image,
  ModalBody,
  ModalFooter,
  Skeleton,
  Stack,
  Text,
  useBoolean,
} from '@chakra-ui/react';
import { EditorStoreContext } from '@adflow/editor';
import React from 'react';
import { StepProps } from '..';
import ImgElTypeRadioGroup, { ImgElType } from './ImgElTypeRadioGroup';
import DataMappingMagic from './DataMappingMagic';
import { elementSelectionHelper } from './elementSelection.helpers';
import { UserContext } from '@adflow/utils';
import { CasinoIcon } from '@adflow/editor/src/icons/casino';

export type StepThreeSubmit = (
  customElements: Array<AdElement>,
  thumbnailURL: string,
  dataSourceTypes?: Array<Datasources.SourceType>,
) => void;

interface StepThreeProps extends StepProps {
  onSubmit: StepThreeSubmit;
}

const ComponentImportStepThree: FC<PropsWithChildren<StepThreeProps>> = ({ onClose, onSubmit }) => {
  const ctx = useContext(EditorStoreContext);
  const state = useComponentImportState();
  const [teamAssetElIds, setTeamAssetElIds] = useState<Array<string>>([]);
  const [casinoAssetElIds, setCasinoAssetElIds] = useState<Array<string>>([]);
  const [isSaved, setIsSaved] = useState(false);
  const dispatch = useComponentImportDispatch();
  const client = useAPIClient();

  const [casinoTemplatesEnabled, setCasinoTemplatesEnabled] = useState(false);
  const { user } = useContext(UserContext);

  const isRunningInsideCypress =
    typeof window !== 'undefined' && Boolean((window as unknown as { Cypress?: boolean }).Cypress);

  useEffect(() => {
    const fetchCasinoTemplateFeature = async () => {
      const hasCasinoTemplate = (await user?.hasFeature('cm-casino-templates')) ?? false;
      setCasinoTemplatesEnabled(hasCasinoTemplate || isRunningInsideCypress);
    };
    fetchCasinoTemplateFeature();
  }, [user]);

  const toggleSaveComponent = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setIsSaved(e.target.checked);
  }, []);

  const suggestedDataSources = useMemo(
    () =>
      Datasources.suggestDataSources([
        ...state.elements.filter(isTextElement),
        ...state.elements.filter(isImageElement),
      ]),
    [state.elements],
  );

  const dataSourceTypes = useMemo(() => {
    return suggestedDataSources.map((ds) => {
      return ds[0].type;
    });
  }, [suggestedDataSources]);

  const [mappingEnabled, { toggle: toggleDataMapping }] = useBoolean(
    suggestedDataSources.length > 0,
  );

  const [checkedElements, setCheckedElements] = useState(
    [...state.elements]
      .map((el) => {
        return { selected: el.type === 'SVG', ...el };
      })
      .sort((a, b) => {
        if (a.selected && !b.selected) return -1;
        if (!a.selected && b.selected) return 1;
        // Sort by type >> name
        return a.type.localeCompare(b.type) || a.name.localeCompare(b.name);
      })
      // HACK: Text elements must always be layered on above images. The above
      // sort will not guarantee this.
      .map((el) => ({
        ...el,
        selected:
          suggestedDataSources.some((dps) => dps.some((dp) => dp.elId === el.id)) ||
          el.type === 'SVG',
      })),
  );

  const lookupDataSource = useCallback(
    (elId: string) => {
      const dataPoints = suggestedDataSources
        // find sources that contain the current element
        .find((dps) => dps.some((dp) => dp.elId === elId))
        // find the data point for that element
        ?.filter((dp) => dp.elId === elId);

      // If no data points are found, return the element as is
      if (!dataPoints) {
        return {};
      }
      const dataPoint = dataPoints[0];

      // Find the data source that matches the type of the data point
      const dataSource = ctx.dataSources.find((ds) => ds.type === dataPoint.type);
      if (!dataSource) {
        return {};
      }
      return { dataSource, dataPoint };
    },
    [suggestedDataSources],
  );

  const mapTeamAssetElement = useCallback(
    (el: AdElementImage) => {
      let autoImportOverrides = { data: {} };
      if (mappingEnabled) {
        const { dataSource, dataPoint } = lookupDataSource(el.id);
        if (dataSource && dataPoint) {
          autoImportOverrides = {
            data: {
              content: '',
              source: elementSelectionHelper.mapDataPointToTeamAssetSource(dataPoint.dataPointName),
            },
          };
        }
      }
      const val: AdElementTeamAsset = {
        ...el,
        type: 'TEAM_ASSET',
        data: {
          ...el.data,
          ...autoImportOverrides.data,
        },
      };
      return val;
    },
    [mappingEnabled],
  );

  const mapCasinoAssetElement = useCallback(
    (el: AdElementImage) => {
      let autoImportOverrides = { data: {} };
      if (mappingEnabled) {
        const { dataSource, dataPoint } = lookupDataSource(el.id);
        if (dataSource && dataPoint) {
          autoImportOverrides = {
            data: {
              content: '',
              source: elementSelectionHelper.mapDataPointToCasinoAssetSource(
                dataPoint.dataPointName,
              ),
            },
          };
        }
      }
      const val: AdElementCasinoAsset = {
        ...el,
        position: {
          ...el.position,
          z: -1, // Ensure Casino assets are always on the bottom
        },
        type: 'CASINO_ASSET',
        data: {
          ...el.data,
          ...autoImportOverrides.data,
        },
      };
      return val;
    },
    [mappingEnabled],
  );

  const mapTextElement = useCallback(
    (el: AdElementText) => {
      let autoImportOverridesData = {};
      let autoImportOverridesSources: {
        sourceId: string;
        name: Datasources.AllowableDataPoints;
      }[] = [];

      if (mappingEnabled) {
        const { dataSource, dataPoint } = lookupDataSource(el.id);
        if (dataSource && dataPoint) {
          autoImportOverridesData = {
            content: `{${dataSource.id}:${dataPoint.dataPointName}}`,
          };
          autoImportOverridesSources = [
            {
              sourceId: dataSource.id,
              name: dataPoint.dataPointName as Datasources.AllowableDataPoints,
            },
          ];
        }
      }
      const val: AdElementText = {
        ...el,
        data: {
          ...el.data,
          ...autoImportOverridesData,
        },
        sources: [...el.sources, ...autoImportOverridesSources],
      };
      return val;
    },
    [mappingEnabled],
  );

  const getDisplayOptionsAssetType = useCallback((elements: AdElement[]) => {
    const matches = elements.filter((el) => isTeamAssetElement(el));
    if (!matches) {
      return;
    }
    return matches
      .map((el) => {
        const { dataPoint } = lookupDataSource(el.id);
        if (dataPoint) {
          return elementSelectionHelper.mapDataPointToDisplayAssetType(dataPoint.dataPointName);
        }
      })
      .filter((x) => x)
      .pop();
  }, []);

  const handleSubmit: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    async (evt) => {
      evt.preventDefault();
      const svgElements = state.elements.filter(isSVGElement);
      const textElements = state.elements.filter(isTextElement);
      const imageElements = state.elements.filter(isImageElement);
      const teamAssetElements = state.elements.filter(isTeamAssetElement);
      const casinoAssetElements = state.elements.filter(isCasinoAssetElement);
      const vectorElements = state.elements.filter(isVectorElement);

      const filteredElements = checkedElements
        .filter((el) => el.selected)
        .map((el) => {
          // no need to retain the selected state
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { selected, ...rest } = el;

          if (isTextElement(rest)) {
            return mapTextElement(rest);
          } else if (isImageElement(rest)) {
            const isTeamAsset = teamAssetElIds.includes(rest.id);
            const isCasinoAsset = casinoAssetElIds.includes(rest.id);
            return isTeamAsset
              ? mapTeamAssetElement(rest)
              : isCasinoAsset
              ? mapCasinoAssetElement(rest)
              : rest;
          } else if (isVectorElement(rest)) {
            return elementSelectionHelper.mapVectorElement(rest, svgElements[0].data.svg);
          } else {
            return rest;
          }
        });

      if (mappingEnabled) {
        const assetType = getDisplayOptionsAssetType(filteredElements);
        if (assetType) {
          ctx.store?.getState().setDisplayOptions('teamAssets', {
            type: assetType,
            fallback: 'NONE',
          });
        }
      }
      onSubmit(
        filteredElements,
        state.componentThumbnailURL,
        mappingEnabled ? dataSourceTypes : [],
      );
      if (!isSaved) {
        return;
      }

      if (!svgElements.length) {
        console.warn('expected at least one SVG element but received 0 ');
        return;
      }
      if (!textElements.length) {
        console.warn('expected at least one text element but received 0 ');
        return;
      }
      if (!imageElements.length) {
        console.warn('expected at least one text element but received 0 ');
        return;
      }
      const svgElement = svgElements[0];
      const svgString = svgElement.data.svg;
      try {
        await client.upsertFigmaComponent({
          figmaId: svgElement.id,
          fileId: state.fileId,
          name: svgElement.name,
          svg: svgString,
          svgUrl: state.previewURL,
          thumbnail: state.componentThumbnailURL,
          svgElement,
          textElements,
          imageElements,
          teamAssetElements,
          casinoAssetElements,
          vectorElements,
        });
      } catch (error) {
        dispatch({
          type: 'error',
          error: 'An unexpected error occurred',
        });
      }
    },
    [
      state.elements,
      state.componentThumbnailURL,
      state.fileId,
      state.previewURL,
      checkedElements,
      onSubmit,
      dataSourceTypes,
      isSaved,
      teamAssetElIds,
      casinoAssetElIds,
      mappingEnabled,
      suggestedDataSources,
      ctx.dataSources,
      client,
      dispatch,
    ],
  );

  const onChangeHandle = useCallback(
    (inputIndex: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setCheckedElements(
        checkedElements.map((el, idx) => {
          if (inputIndex === idx) {
            return { ...el, selected: e.target.checked };
          }
          return el;
        }),
      );
    },
    [checkedElements],
  );

  useEffect(() => {
    if (!mappingEnabled) {
      return;
    }
    checkedElements.forEach((el) => {
      const match = suggestedDataSources.find((dps) => dps.some((dp) => dp.elId === el.id));
      if (!match) {
        return;
      }
      match.forEach((dp) => {
        if (dp.type === 'CASINO' && casinoTemplatesEnabled) {
          setCasinoAssetElIds((prev) => [...prev.filter((id) => id !== dp.elId), dp.elId]);
        }
        if (elementSelectionHelper.isTeamAssetDataPoint(dp.dataPointName)) {
          setTeamAssetElIds((prev) => [...prev.filter((id) => id !== dp.elId), dp.elId]);
        }
      });
      return;
    });
    return;
  }, [suggestedDataSources, checkedElements, mappingEnabled, casinoTemplatesEnabled]);

  const handleToggleImgElType = (elId: string, newType: ImgElType) => {
    const elIsTeamAssetEl = teamAssetElIds.includes(elId);
    const elIsCasinoEl = casinoAssetElIds.includes(elId);

    if (newType === 'teamasset') {
      if (!elIsTeamAssetEl) {
        setTeamAssetElIds((prev) => [...prev, elId]);
      }
      if (elIsCasinoEl) {
        setCasinoAssetElIds((prev) => prev.filter((id) => id !== elId));
      }

      return;
    }

    if (newType === 'img') {
      if (elIsTeamAssetEl) {
        setTeamAssetElIds((prev) => prev.filter((id) => id !== elId));
      }
      if (elIsCasinoEl) {
        setCasinoAssetElIds((prev) => prev.filter((id) => id !== elId));
      }
    }

    if (newType === 'casino') {
      if (!elIsCasinoEl) {
        setCasinoAssetElIds((prev) => [...prev, elId]);
      }
      if (elIsTeamAssetEl) {
        setTeamAssetElIds((prev) => prev.filter((id) => id !== elId));
      }
    }
  };

  const onToggleMapping = useCallback(
    (selected: boolean) => {
      console.log({ selected, suggestedDataSources });
      setCheckedElements(
        checkedElements.map((el) => {
          const match = suggestedDataSources.find((dps) => dps.some((dp) => dp.elId === el.id));
          if (match) {
            return { ...el, selected: selected };
          }
          return el;
        }),
      );
      toggleDataMapping();
    },
    [checkedElements, toggleDataMapping, suggestedDataSources],
  );

  const getImageElementType = useCallback(
    (elId: string) => {
      if (!mappingEnabled) {
        return;
      }
      const match = suggestedDataSources.find((dps) => dps.some((dp) => dp.elId === elId));
      if (!match) {
        return;
      }

      if (casinoTemplatesEnabled && match.some((dp) => dp.type === 'CASINO')) {
        return 'casino';
      }

      return match.some((dp) =>
        elementSelectionHelper.mapDataPointToTeamAssetSource(dp.dataPointName),
      )
        ? 'teamasset'
        : 'img';
    },
    [suggestedDataSources, checkedElements, mappingEnabled, casinoTemplatesEnabled],
  );

  return (
    <>
      <ModalBody display="flex" alignItems="flex-start" columnGap={5}>
        <Box>
          {state.previewURL && (
            <Image
              data-test-id="loaded-image"
              w={300}
              src={state.previewURL}
              fallback={<Skeleton height={300} width={300} />}
            ></Image>
          )}
          {!state.savedComponentId && (
            <Checkbox mt={3} key="toggleSave" onChange={toggleSaveComponent}>
              <Box display="flex" alignItems="center">
                <FaBookmark /> <Text ml={1}>Save this component for later</Text>
              </Box>
            </Checkbox>
          )}
        </Box>
        <Box flexDirection={'column'}>
          <Box
            borderWidth={1}
            borderRadius="md"
            px={4}
            py={3}
            flex={1}
            data-test-id="select-element"
          >
            <Heading as="h3" size="md">
              Element Selection
            </Heading>
            <Text size="md" mb={3}>
              Select any additional elements you would like to create.
            </Text>
            <Stack
              direction="column"
              maxH={'300px'}
              overflowY={'scroll'}
              data-test-id="elements-list"
            >
              {checkedElements.map((el, index) => {
                if (el.type === 'SVG') {
                  return (
                    <Checkbox pl={1} key={el.id} defaultChecked isDisabled>
                      <Box display={'flex'} alignItems="center">
                        <FaShapes /> <Text ml={2}>{el.name}</Text>
                      </Box>
                    </Checkbox>
                  );
                }
                if (el.type === 'TEXT') {
                  return (
                    <Checkbox
                      pl={1}
                      key={el.id}
                      isChecked={el.selected}
                      onChange={onChangeHandle(index)}
                    >
                      <Box display={'flex'} alignItems="center">
                        <Icon as={LuType}></Icon>
                        <Text ml={2} noOfLines={1} as="span">
                          {el.name}
                          <Text ml={2} fontStyle="italic" fontSize="xs" as="span">
                            - &quot;{el.data.content}&quot;
                          </Text>
                        </Text>
                      </Box>
                    </Checkbox>
                  );
                }
                if (el.type === 'IMAGE') {
                  return (
                    <Flex key={el.id} justifyContent="space-between">
                      <Checkbox pl={1} isChecked={el.selected} onChange={onChangeHandle(index)}>
                        <Box display={'flex'} alignItems="center">
                          {teamAssetElIds.includes(el.id) ? (
                            <GiAmericanFootballHelmet />
                          ) : casinoAssetElIds.includes(el.id) ? (
                            <CasinoIcon />
                          ) : (
                            <LuImage />
                          )}
                          <Text ml={2}>{el.name}</Text>
                        </Box>
                      </Checkbox>
                      <ImgElTypeRadioGroup
                        onChange={(type) => handleToggleImgElType(el.id, type)}
                        name={`${el.id}_assettype-toggle`}
                        val={getImageElementType(el.id)}
                        casinoTemplatesEnabled={casinoTemplatesEnabled}
                      />
                    </Flex>
                  );
                }
                if (el.type === 'VECTOR') {
                  return (
                    <Checkbox
                      pl={1}
                      key={el.id}
                      isChecked={el.selected}
                      onChange={onChangeHandle(index)}
                    >
                      <Box display={'flex'} alignItems="center">
                        <PiBoundingBox />
                        <Text ml={2}>{el.name}</Text>
                      </Box>
                    </Checkbox>
                  );
                }
                return null;
              })}
            </Stack>
          </Box>
          <Box
            borderWidth={1}
            borderRadius="md"
            mt={2}
            px={4}
            py={3}
            flex={1}
            data-test-id="select-element"
          >
            <DataMappingMagic
              mappingEnabled={mappingEnabled}
              onToggleMapping={onToggleMapping}
              suggestedDataSources={suggestedDataSources}
            />
          </Box>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Button data-test-id="close-button" variant="ghost" mr={3} onClick={onClose}>
          Close
        </Button>
        <Button colorScheme="blue" onClick={handleSubmit} data-test-id="finish-selection">
          Finish
        </Button>
      </ModalFooter>
    </>
  );
};

ComponentImportStepThree.displayName = 'ComponentImportStepThree';
export default ComponentImportStepThree;
